import { useState } from 'react'

import { faEye, faGavel } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { formatInTimeZone } from 'date-fns-tz'
import { FormattedNumber } from 'react-intl'
import { styled } from 'styled-components'

import { ContractDtoBidBasisEnum } from '@b-stock/contract-api-client'
import type { PurchasingPhase } from '@b-stock/fe-core/bstock-next/helpers'
import type { OrchestratedAuctionListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import {
  Duration,
  FormattedMessage,
  parseOptionalISODate,
  Tooltip,
  usePrecisionNumberFormat,
} from '@b-stock/fe-core/bstock-react'
import { Colors, Shadows } from '@b-stock/fe-core/design-system'

import { useBidFormContext } from '@/buyer-portal/components/features/listing/actions/auction/BidFormProvider'
import { AuctionState } from '@/buyer-portal/components/features/listing/actions/auction/const'
import { PermissionGranule, usePermission } from '@/helpers/permissions'

import { AuctionStatisticModal } from '../../Bid/StatsBar/AuctionStatisticModal'
import {
  AmountValue,
  Column,
  CountdownValue,
  Header,
  InfoIcon,
  Label,
  MsrpRow,
  MsrpText,
  StatButton,
  StatItem,
  SubRow,
} from '../ui'

const PopcornTooltip = styled(Tooltip)`
  ${Shadows.Popovers}
  background: ${Colors.Semantic.Neutral.black};
  color: ${Colors.Semantic.Neutral.white};
  padding: 1.5rem;
  pointer-events: initial;
  white-space: pre-line;
`

const ns = 'Listing.actionColumn.actionCard.bid'
const clientTimeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone
const dateFormat = 'MM/dd/yy'

export const BidHeader = ({
  displayPhase,
  hasBanner,
  isPopcorn,
  listing,
  priceOverride,
}: {
  displayPhase: PurchasingPhase
  hasBanner?: boolean
  isPopcorn?: boolean
  listing: OrchestratedAuctionListingDetails
  /** Replaces the displayed price, e.g. the First Dibs early buy-now price. */
  priceOverride?: number
}) => {
  const { details } = useBidFormContext()
  const {
    bidCount,
    viewCount,
    currency,
    bidBasis,
    bidUnit,
    bidPrecision,
    currentPrice,
    msrpPrice,
    bidDeadline,
    isEnded,
    auctionState,
  } = details

  const [countdownRef, setCountdownRef] = useState<HTMLSpanElement | null>(null)
  const [showStatisticModal, setShowStatisticModal] = useState(false)
  const canViewAuctionStatistics = usePermission(
    PermissionGranule.FEBP_LISTING_DETAIL_AUCTION_STATS
  )

  const isCanceled = auctionState === AuctionState.Canceled
  const isRelativeBid = bidBasis === ContractDtoBidBasisEnum.Relative
  const canceledTime =
    'canceledTime' in listing.auction ? listing.auction.canceledTime : null
  const targetDate = parseOptionalISODate(
    isCanceled ? canceledTime : bidDeadline
  )
  const isBinoDisplay = displayPhase === 'BINO'
  // Regular Buy Now price, shown under a differing First Dibs early price.
  const regularBuyNowPrice = listing.auction.buyNow?.price ?? 0
  const afterWindowPrice =
    priceOverride !== undefined &&
    isBinoDisplay &&
    regularBuyNowPrice > 0 &&
    priceOverride !== regularBuyNowPrice
      ? regularBuyNowPrice
      : undefined
  const price =
    priceOverride ??
    (isBinoDisplay
      ? (listing.auction.buyNow?.price ?? currentPrice)
      : currentPrice)
  const showPopcornWarning = !!isPopcorn && !isBinoDisplay && !isEnded
  const currentPricePercent =
    msrpPrice && price ? price / (msrpPrice / 100) : null

  const { formatPrecisionNumber } = usePrecisionNumberFormat({
    precision: bidPrecision,
    precisionStored: 1,
    format: isRelativeBid ? 'percent' : 'currency',
    currency,
  })

  return (
    <Header $hasBanner={hasBanner}>
      <Column>
        <Label>
          <FormattedMessage
            id={`${ns}.header.${isEnded ? (isCanceled ? 'canceledOn' : 'endedOn') : 'closesIn'}`}
          />
        </Label>
        <CountdownValue
          ref={setCountdownRef}
          $warning={showPopcornWarning}
          suppressHydrationWarning
        >
          {isEnded && targetDate ? (
            formatInTimeZone(targetDate, clientTimeZone, dateFormat)
          ) : targetDate ? (
            <Duration format="short" target={targetDate} />
          ) : (
            '—'
          )}
        </CountdownValue>
        {showPopcornWarning && countdownRef && (
          <PopcornTooltip
            referenceElement={countdownRef}
            containerElement={countdownRef}
          >
            <FormattedMessage id="Auction.details.statsBar.tooltip.popcornPopover" />
          </PopcornTooltip>
        )}
        <SubRow>
          {/* Gated like V1's BidCount — the count is auction statistics. */}
          {typeof bidCount !== 'undefined' &&
            canViewAuctionStatistics &&
            !isBinoDisplay && (
              <StatButton
                onClick={() => setShowStatisticModal(true)}
                type="button"
              >
                <FontAwesomeIcon icon={faGavel} />
                <FormattedMessage
                  id={`${ns}.header.bids`}
                  values={{ bids: bidCount }}
                />
              </StatButton>
            )}
          {typeof viewCount !== 'undefined' && (
            <StatItem>
              <FontAwesomeIcon icon={faEye} />
              <FormattedMessage
                id={`${ns}.header.views`}
                values={{ views: viewCount }}
              />
            </StatItem>
          )}
        </SubRow>
      </Column>

      <Column $alignRight>
        <Label>
          <FormattedMessage
            id={`${ns}.header.${isBinoDisplay ? 'buyNowPrice' : 'currentBid'}`}
          />
        </Label>
        <AmountValue>
          {isRelativeBid ? (
            <FormattedNumber
              value={price / 100}
              style="percent"
              minimumFractionDigits={2}
              maximumFractionDigits={2}
            />
          ) : (
            formatPrecisionNumber(price)
          )}
          {bidUnit && (
            <FormattedMessage
              id={`Contracts.Enums.perUnit.shortSentenceCase.${bidUnit}`}
            />
          )}
        </AmountValue>
        {afterWindowPrice !== undefined && (
          <MsrpRow>
            <MsrpText>
              <FormattedMessage
                id={`${ns}.header.afterWindowPrice`}
                values={{
                  price: formatPrecisionNumber(afterWindowPrice),
                }}
              />
            </MsrpText>
          </MsrpRow>
        )}
        {!isRelativeBid && currentPricePercent !== null && (
          <MsrpRow>
            <MsrpText>
              <FormattedMessage
                id={`${ns}.header.percentOfMsrp`}
                values={{
                  value: (
                    <FormattedNumber
                      value={currentPricePercent}
                      style="percent"
                      minimumFractionDigits={1}
                      maximumFractionDigits={1}
                    />
                  ),
                }}
              />
            </MsrpText>
            <InfoIcon size="small" tooltipProps={{ placement: 'bottom' }}>
              <FormattedMessage
                id={`${ns}.header.${isBinoDisplay ? 'percentOfMsrpTooltipBuyNow' : 'percentOfMsrpTooltip'}`}
              />
            </InfoIcon>
          </MsrpRow>
        )}
      </Column>

      {showStatisticModal && (
        <AuctionStatisticModal
          closeModal={() => setShowStatisticModal(false)}
          currency={currency}
          listing={listing}
        />
      )}
    </Header>
  )
}
